/**
 * Componente del header para la pantalla Radar.
 * Muestra el avatar del usuario, notificaciones y controles.
 * Memoizado para evitar renderizaciones innecesarias.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {function} props.onOpenProfile - Función para abrir el drawer de perfil
 * @param {function} props.onOpenPreferences - Función para abrir el drawer de preferencias
 */

import { memo, useState, useEffect, useMemo } from 'react';
import { MdTune } from "react-icons/md";
import { TbReload, TbWorldPin } from "react-icons/tb";
import { useAuth } from '../../../hooks/useAuth';
import MyUserAvatar from './MyUserAvatar';
import ExplorerModal from './ExplorerModal';
import {
  getLocationState,
  setExplorerLocation,
  disableExplorerMode,
  syncUserGeo
} from '../../../hooks/location/useLocationState';

function RadarHeader({ onOpenProfile, onOpenPreferences, onRefresh }) {
  const { user } = useAuth();
  const [showExplorerModal, setShowExplorerModal] = useState(false);
  const [isExplorerActive, setIsExplorerActive] = useState(false);
  
  // Verificar estado de Explorer al montar
  useEffect(() => {
    const state = getLocationState();
    setIsExplorerActive(state?.explorerActive || false);
  }, []);
  
  /**
   * Maneja el clic en el botón Explorer
   * Siempre abre el modal para ver/modificar la ubicación explorada
   */
  const handleExplorerClick = () => {
    // Abrir modal para seleccionar/modificar ubicación
    setShowExplorerModal(true);
  };
  
  /**
   * Maneja la confirmación de ubicación seleccionada
   */
  const handleExplorerConfirm = async (lat, lng) => {
    try {
      setExplorerLocation(lat, lng);
      const location = { lat, lng };
      await syncUserGeo(location, user.id);
      setIsExplorerActive(true);
      setShowExplorerModal(false);
      // Recargar radar
      window.location.reload();
    } catch (e) {
      console.error('[RadarHeader] Error confirming explorer location:', e);
    }
  };
  
  /**
   * Maneja la desactivación del modo Explorer
   */
  const handleExplorerDeactivate = async () => {
    try {
      disableExplorerMode();
      setIsExplorerActive(false);
      setShowExplorerModal(false);
      // Recargar radar
      window.location.reload();
    } catch (e) {
      console.error('[RadarHeader] Error deactivating explorer:', e);
    }
  };
  
  // Obtener la ubicación actual del explorer si está activo (memoizado)
  const currentExplorerLocation = useMemo(() => {
    const state = getLocationState();
    return state?.explorer || null;
  }, [isExplorerActive]);

  return (
    <div className="sticky top-0 z-10 h-20 flex items-center justify-between p-2 bg-neutral-900 border-b border-neutral-800">
      {/* Avatar del usuario actual */}
      <button
        onClick={onOpenProfile}
        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
      >
        <MyUserAvatar />
      </button>

      {/* Controles del lado derecho */}
      <div className="flex items-center gap-2">

        {/* Botón Reload */}
        <button
          onClick={onRefresh}
          className={'p-2 rounded-full transition-colors bg-neutral-800/90 hover:bg-neutral-900'}
          aria-label={"Recargar"}
        >
          <TbReload
            size={24}
            className={"opacity-60"}
            color="white"
          />
        </button>

        {/* Botón Explorer */}
        <button
          onClick={handleExplorerClick}
          className={`p-2 rounded-full transition-colors flex items-center gap-2 ${
            isExplorerActive 
              ? 'bg-red-500/90 hover:bg-red-600' 
              : 'bg-neutral-800/90 hover:bg-neutral-900'
          }`}
          aria-label={isExplorerActive ? "Desactivar Explorer" : "Activar Explorer"}
        >
          <TbWorldPin 
            size={24} 
            className={isExplorerActive ? "text-white" : "opacity-60"} 
            color="white" 
          />
          <span className="text-white text-sm pr-1 hidden md:inline">Explorer</span>
        </button>

        {/* Botón para abrir el drawer de configuración */}
        <button
          onClick={() => {
            console.log('[RadarHeader] Settings button pressed');
            onOpenPreferences();
          }}
          className="p-2 hover:bg-neutral-800 bg-neutral-800/90 rounded-full transition-colors flex items-center gap-2"
          aria-label="Abrir preferencias"
        >
          <MdTune size={24} className="opacity-60" color="white" />
          <span className="text-white text-sm pr-1 hidden md:inline">Preferencias</span>
        </button>
      </div>
      
      {/* Modal Explorer */}
      {showExplorerModal && (
        <ExplorerModal
          onClose={() => setShowExplorerModal(false)}
          onConfirm={handleExplorerConfirm}
          onDeactivate={handleExplorerDeactivate}
          currentExplorerLocation={currentExplorerLocation}
        />
      )}
    </div>
  );
}

// Memoizar el componente para evitar renders innecesarios
export default memo(RadarHeader);
